import { useDispatch, useSelector } from 'react-redux';

import { Dropdown } from '@opendoor/bricks-next';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import { FlowQuestion, SelectQuestion } from 'src/lib/flow/FlowQuestion';
import { checkValidation, ValidationResult } from 'src/lib/flow/validation';
import { getAnswers } from 'src/store/sellerFlow/selectors';
import { changeAnswers } from 'src/store/sellerInput';

export type SelectInputProps = {
  question: SelectQuestion;
  value?: string;
  isLoading?: boolean;
  showValidation?: boolean;
};

const SelectInput: React.FC<SelectInputProps> = ({ question, value, showValidation }) => {
  const {
    key,
    placeholder,
    label,
    helperText,
    minValue,
    maxValue,
    maxHasPlus,
    options = [],
  } = question;
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);

  const { options: selectOptions, shouldPullValue } = getDropdownSelectOptions(
    options,
    minValue,
    maxValue,
    maxHasPlus,
    key,
  );

  const onChange = (selectedValue: string) => {
    if (shouldPullValue) {
      const opt = options.find(({ label, value }) => label === value);
      if (opt) {
        selectedValue = opt.value.toString();
      }
    }

    const derivedValue = getDerivedValue(selectedValue.toString());

    if (answers[key] !== derivedValue) {
      dispatch(changeAnswers([{ key, answer: derivedValue ?? null }]));
    }
    trackAnswerChange(key, derivedValue);
  };

  let result: ValidationResult | undefined;
  let errorText: string | undefined;
  if (showValidation) {
    result = checkValidation(question as FlowQuestion, value);
    errorText = getErrorMessage(result);
  }

  return (
    <Dropdown
      errorText={errorText}
      invalid={showValidation && !result?.isValid}
      name={key}
      id={key}
      placeholderOption={{ label: placeholder || 'Select...' }}
      onValueChange={onChange}
      options={selectOptions}
      title={label || ''}
      supportText={helperText}
      prefilledValue={value?.toString() ?? ''}
      size="large"
    />
  );
};

export const getDropdownSelectOptions = (
  options: SelectQuestion['options'],
  min?: number,
  maxValue?: number,
  showMaxPlus = false,
  key?: string,
): {
  options: Array<{ label: string; value: string }>;
  shouldPullValue: boolean;
} => {
  let shouldPullValue = false;
  let answerOptions = (options ?? []).map(({ label, value }) => {
    if (typeof value === 'object') {
      shouldPullValue = true;
    }
    return { label, value: typeof value === 'object' ? label : value.toString() };
  });

  // This is needed since the Years dropdown dynamically calculates
  // the max value, but that is always returned as a string
  const max = maxValue;
  if (min !== undefined && max !== undefined) {
    answerOptions = [];
    for (let i = min; i <= max; i++) {
      answerOptions.push({
        label: `${i.toString()}${i == max && showMaxPlus ? '+' : ''}`,
        value: i.toString(),
      });
    }
  }

  // Display year options in descending order
  if (key === 'home.year_built') {
    answerOptions.reverse();
  }

  return { options: answerOptions, shouldPullValue };
};

// bricks-next dropdown requires all option's values to be strings
// but we want to save the answer as a number or boolean when they are
const getDerivedValue = (value: string) => {
  let derivedValue: string | number | boolean = value;

  if (isNaN(parseInt(value))) {
    derivedValue = value;
  } else if (value === 'true') {
    derivedValue = true;
  } else if (value === 'false') {
    derivedValue = false;
  } else {
    derivedValue = parseInt(value);
  }

  return derivedValue;
};

export default SelectInput;
